import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useShellWidth } from "@/components/layout/shell-width";
import { toast } from "@/components/ui/toast";
import {
  api,
  ApiError,
  type CaseVersion,
} from "@/lib/api";
import { useAuth } from "@/lib/auth";

export type PhoneJsonEditorSessionValue = {
  open: boolean;
  setOpen: (open: boolean) => void;
  dirty: boolean;
  setDirty: (dirty: boolean) => void;
  saveBusy: boolean;
  saveError: string | null;
  canEdit: boolean;
  readOnlyReason: string | null;
  phoneJson: unknown | null;
  placeholderCount: number;
  mediaReadyOk: boolean | null;
  phoneRuntimeVersion: string | null;
  caseId: string;
  versionId: string;
  versionStatus: string | null;
  previewReloadKey: number;
  onSave: (phoneJson: unknown) => Promise<void>;
};

const PhoneJsonEditorSessionContext =
  createContext<PhoneJsonEditorSessionValue | null>(null);

export function PhoneJsonEditorSessionProvider({
  caseId,
  versionId,
  version,
  children,
}: {
  caseId: string;
  versionId: string;
  version: CaseVersion;
  children: ReactNode;
}) {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const { setWide } = useShellWidth();
  const [open, setOpenState] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saveBusy, setSaveBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [previewReloadKey, setPreviewReloadKey] = useState(0);

  const versionStatus = version.status ?? null;
  const canLoadPackage =
    Boolean(version.phoneJsonR2Key) ||
    Boolean(version.hasPipeline) ||
    versionStatus === "published" ||
    versionStatus === "failed";

  const phoneJsonQuery = useQuery({
    queryKey: ["phone-json", versionId],
    queryFn: () => api.getPhoneJson(versionId),
    enabled: auth.status === "authed" && Boolean(versionId) && canLoadPackage,
  });

  const mediaReadyQuery = useQuery({
    queryKey: ["media-readiness", versionId],
    queryFn: () => api.getMediaReadiness(versionId),
    enabled: auth.status === "authed" && Boolean(versionId) && canLoadPackage,
    refetchInterval: open ? 5000 : false,
  });

  useEffect(() => {
    setDirty(false);
    setSaveError(null);
    setOpenState(false);
  }, [versionId]);

  useEffect(() => {
    setWide(open);
    return () => setWide(false);
  }, [open, setWide]);

  const setOpen = useCallback((next: boolean) => {
    setOpenState(next);
  }, []);

  const phoneJson = phoneJsonQuery.data?.phoneJson ?? null;
  const canEdit =
    Boolean(versionId) && versionStatus !== "superseded" && phoneJson != null;

  const onSave = useCallback(
    async (nextPhoneJson: unknown) => {
      if (!versionId || saveBusy || !dirty) return;
      setSaveBusy(true);
      setSaveError(null);
      try {
        const result = await api.savePhoneJson(versionId, {
          phoneJson: nextPhoneJson,
        });
        setDirty(false);
        toast.add({
          type: "success",
          title: "已保存",
          description:
            versionStatus === "published" || result.opapi
              ? "Phone JSON 已保存并通过 OPAPI 重新导入。"
              : "Phone JSON 已保存。预览小手机将重新加载。",
        });
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: ["phone-json", versionId],
          }),
          queryClient.invalidateQueries({
            queryKey: ["media-readiness", versionId],
          }),
          queryClient.invalidateQueries({
            queryKey: ["publish-gate", versionId],
          }),
          queryClient.invalidateQueries({ queryKey: ["case", caseId] }),
          queryClient.invalidateQueries({
            queryKey: ["case-preview", caseId],
          }),
          queryClient.invalidateQueries({ queryKey: ["json-edits", caseId] }),
        ]);
        setPreviewReloadKey((k) => k + 1);
      } catch (err) {
        setSaveError(
          err instanceof ApiError ? err.code : "phone_json_save_failed",
        );
      } finally {
        setSaveBusy(false);
      }
    },
    [versionId, saveBusy, dirty, versionStatus, queryClient, caseId],
  );

  const value = useMemo<PhoneJsonEditorSessionValue>(
    () => ({
      open,
      setOpen,
      dirty,
      setDirty,
      saveBusy,
      saveError,
      canEdit,
      readOnlyReason:
        versionStatus === "superseded"
          ? "此已取代版本为只读。"
          : "此版本为只读。",
      phoneJson,
      placeholderCount: phoneJsonQuery.data?.placeholderCount ?? 0,
      mediaReadyOk: mediaReadyQuery.data ? mediaReadyQuery.data.ok : null,
      phoneRuntimeVersion: version.phoneRuntimeVersion ?? null,
      caseId,
      versionId,
      versionStatus,
      previewReloadKey,
      onSave,
    }),
    [
      open,
      setOpen,
      dirty,
      saveBusy,
      saveError,
      canEdit,
      phoneJson,
      phoneJsonQuery.data?.placeholderCount,
      mediaReadyQuery.data,
      version.phoneRuntimeVersion,
      caseId,
      versionId,
      versionStatus,
      previewReloadKey,
      onSave,
    ],
  );

  return (
    <PhoneJsonEditorSessionContext.Provider value={value}>
      {children}
    </PhoneJsonEditorSessionContext.Provider>
  );
}

export function usePhoneJsonEditorSession(): PhoneJsonEditorSessionValue {
  const ctx = useContext(PhoneJsonEditorSessionContext);
  if (!ctx) {
    throw new Error(
      "usePhoneJsonEditorSession must be used within PhoneJsonEditorSessionProvider",
    );
  }
  return ctx;
}

export function usePhoneJsonEditorSessionOptional(): PhoneJsonEditorSessionValue | null {
  return useContext(PhoneJsonEditorSessionContext);
}
